import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiLoader, FiTrash2, FiEdit2 } from 'react-icons/fi';
import ConfirmModal from '../../common/ConfirmModal';

interface QuestionAnswer {
    id: number;
    question_id: number;
    question_text: string;
    answere_id: number;
    answer_text: string;
    function?: string;
    created_at: string;
}

interface Option {
    id: number;
    text: string;
    testo: string;
}

const ListQuestionAnswers: React.FC = () => {
    const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>([]);
    // const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<QuestionAnswer | null>(null);
    const [editingItem, setEditingItem] = useState<QuestionAnswer | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [questions, setQuestions] = useState<Option[]>([]);
    const [answers, setAnswers] = useState<Option[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchQuestionAnswers();
        fetchQuestions();
        fetchAnswers();
    }, []);

    const fetchQuestionAnswers = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/question-answers`);
            setQuestionAnswers(response.data);
        } catch (error) {
            setError("Errore durante il caricamento delle domande e risposte.");
        } finally {
            // setLoading(false);
        }
    };

    const fetchQuestions = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/questions`);
            setQuestions(response.data);
        } catch (error) {
            setError("Errore durante il caricamento delle domande.");
        }
    };

    const fetchAnswers = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/answers`);
            setAnswers(response.data);
        } catch (error) {
            setError("Errore durante il caricamento delle risposte.");
        }
    };

    const handleDelete = (qa: QuestionAnswer) => {
        setDeleteConfirm(qa);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/question-answers/${deleteConfirm.id}`);
            setQuestionAnswers((prev) => prev.filter((qa) => qa.id !== deleteConfirm.id));
            setSuccessMessage("Associazione eliminata con successo!");
            setError(null);
            setDeleteConfirm(null);
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            console.error("Errore durante l'eliminazione", error);
        } finally {
            setDeleteConfirm(null);
        }
    };

    const handleEdit = (qa: QuestionAnswer) => {
        setEditingItem(qa);
        setShowEditModal(true);
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>, field: keyof QuestionAnswer) => {
        if (!editingItem) return;
        
        const value = e.target.type === 'select-one' ? Number(e.target.value) : e.target.value;
        setEditingItem(prev => ({
            ...prev!,
            [field]: value
        }));
    };

    const handleUpdate = async () => {
        if (!editingItem) return;
        
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/question_answers/${editingItem.id}`,
                editingItem // Envoyez l'objet complet
            );
            
            setQuestionAnswers(prev =>
                prev.map(qa =>
                    qa.id === editingItem.id ? { ...qa, ...response.data } : qa
                )
            );
            setShowEditModal(false);
            setEditingItem(null);
            setSuccessMessage("Associazione aggiornata con successo!");
            setTimeout(() => setSuccessMessage(null), 3000);
            fetchQuestionAnswers(); // Rafraîchir la liste après la mise à jour
        } catch (error) {
            console.error("Errore durante la messa a giorno:", error);
        }
    };

    const filteredQuestionAnswers = questionAnswers.filter(qa =>
        qa.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        qa.answer_text.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedQuestionAnswers = filteredQuestionAnswers.sort((a, b) => b.id - a.id);

    const totalPages = Math.ceil(sortedQuestionAnswers.length / itemsPerPage);
    const displayedQuestionAnswers = sortedQuestionAnswers.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    // if (loading) {
    //     return (
    //         <div className="flex justify-center items-center h-40">
    //             <FiLoader className="w-8 h-8 animate-spin text-indigo-500" />
    //         </div>
    //     );
    // }

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200/80">
            <div className="px-6 py-4 border-b border-gray-200 my-4">
                <div className="flex items-center">
                    <h2 className="text-xl font-semibold text-gray-800">Elenco delle Domande e Risposte</h2>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Cerca per domanda o risposta..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>
                {successMessage && (
                    <div className="mt-2 mb-4 px-4 py-2 bg-green-100 text-green-700 text-sm rounded">
                        {successMessage}
                    </div>
                )}
                {error && (
                    <div className="mt-2 mb-4 px-4 py-2 bg-red-100 text-red-700 text-sm rounded">
                        {error}
                    </div>
                )}
            </div>
           

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-[rgb(149,71,254)] via-[rgb(89,96,211)] to-[rgb(51,112,183)]">
                        <tr className="border-b border-[rgb(51,112,183)]">
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Domanda</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Risposta</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">Azioni</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {displayedQuestionAnswers.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-8 text-center">
                                    <p className="text-gray-500 text-sm">Nessuna domanda o risposta trovata</p>
                                </td>
                            </tr>
                        ) : (
                            displayedQuestionAnswers.map((qa) => (
                                <tr key={qa.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {(page - 1) * itemsPerPage + displayedQuestionAnswers.indexOf(qa) + 1}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">{qa.question_text}</td>
                                    <td className="px-6 py-4 text-sm text-gray-900">{qa.answer_text}</td>
                                    <td className="px-6 py-4 text-right text-sm">
                                        <button
                                            onClick={() => handleEdit(qa)}
                                            className="p-2 rounded-full hover:bg-indigo-50 text-indigo-600 transition-colors"
                                            title="Modifica"
                                        >
                                            <FiEdit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(qa)}
                                            className="p-2 rounded-full hover:bg-red-50 text-red-600 transition-colors"
                                            title="Elimina"
                                        >
                                            <FiTrash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="px-4 py-4 flex justify-between items-center">
                <button
                onClick={() => setPage(page > 1 ? page - 1 : 1)}
                disabled={page === 1}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
                >
                Precedente
                </button>
                <span className="text-sm text-gray-500">Pagina {page} di {totalPages}</span>
                <button
                onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
                >
                Successivo
                </button>
            </div>

            {showEditModal && editingItem && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-10 rounded-lg w-[700px] shadow-xl">
                        <h3 className="text-3xl font-semibold mb-8 text-indigo-600 text-center">Modificare l'associazione</h3>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-base text-gray-700 font-medium">Domanda</label>
                                <select
                                    value={editingItem.question_id}
                                    onChange={(e) => handleChange(e, 'question_id')}
                                    className="w-full border-2 border-gray-300 rounded-md p-3 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    {questions.map((question) => (
                                        <option key={question.id} value={question.id}>
                                            {question.text}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-base text-gray-700 font-medium">Risposta</label>
                                <select
                                    value={editingItem.answere_id}
                                    onChange={(e) => handleChange(e, 'answere_id')}
                                    className="w-full border-2 border-gray-300 rounded-md p-3 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    {answers.map((answer) => (
                                        <option key={answer.id} value={answer.id}>
                                            {answer.testo}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-base text-gray-700 font-medium">Funzione</label>
                                <input
                                    type="text"
                                    value={editingItem.function || ''}
                                    onChange={(e) => handleChange(e, 'function')}
                                    className="w-full border-2 border-gray-300 rounded-md p-3 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                        <div className="mt-8 flex justify-end space-x-6">
                            <button
                                onClick={handleUpdate}
                                className="px-8 py-3 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition duration-300"
                            >
                                Salvare
                            </button>
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="px-8 py-3 bg-gray-300 text-gray-700 rounded-lg shadow-md hover:bg-gray-400 transition duration-300"
                            >
                                Annulla
                            </button>
                        </div>
                    </div>
                </div>
            )}

         <ConfirmModal
                 isOpen={deleteConfirm !== null}
                 title="Conferma la cancellazione"  
                 message="Sei sicuro di voler eliminare questa domanda?" /* Are you sure you want to delete this question? */
                 onConfirm={confirmDelete}
                 onCancel={() => setDeleteConfirm(null)}
               />
        </div>
    );
};

export default ListQuestionAnswers;